/**
 * RAG Connector configuration component.
 * Specialized configuration for RAG connector (RAGFlow).
 */

// React Imports
import { useState } from 'react';
import { Input } from '@coordio/ui';

export interface RagConnectorConfigProps {
  settingsJson?: string;
  onChange?: (settingsJson: string) => void;
}

/**
 * RAG Connector configuration component.
 */
export const RagConnectorConfig = ({
  settingsJson = '{}',
  onChange,
}: RagConnectorConfigProps) => {
  const [ragFlowUrl, setRagFlowUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [defaultKnowledgeBaseId, setDefaultKnowledgeBaseId] = useState('');
  const [defaultTopK, setDefaultTopK] = useState(5);
  const [defaultScoreThreshold, setDefaultScoreThreshold] = useState(0.7);

  return (
    <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
      <div className="p-6">
        <h6 className="text-lg font-semibold tracking-tight mb-6">
          RAGFlow Configuration
        </h6>

        <div className="flex flex-col gap-4">
          <div className="space-y-1">
            <label className="text-sm font-medium">RAGFlow Server URL</label>
            <Input
              type="text"
              className="w-full"
              value={ragFlowUrl}
              required
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setRagFlowUrl(e.target.value);
                const settings = JSON.parse(settingsJson || '{}');
                settings.ragFlowUrl = e.target.value;
                onChange?.(JSON.stringify(settings));
              }}
            />
            <p className="text-xs text-muted-foreground">Base URL of your RAGFlow server (e.g., https://ragflow.example.com)</p>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">API Key</label>
            <Input
              type="password"
              className="w-full"
              value={apiKey}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setApiKey(e.target.value);
                const settings = JSON.parse(settingsJson || '{}');
                settings.apiKey = e.target.value;
                onChange?.(JSON.stringify(settings));
              }}
            />
            <p className="text-xs text-muted-foreground">RAGFlow API key or bearer token</p>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-border">
          <h6 className="text-lg font-semibold tracking-tight mb-4">
            Knowledge Base Settings
          </h6>
          <div className="flex flex-col gap-4">
            <div className="space-y-1">
              <label className="text-sm font-medium">Default Knowledge Base ID</label>
              <Input
                type="text"
                className="w-full"
                value={defaultKnowledgeBaseId}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setDefaultKnowledgeBaseId(e.target.value);
                  const settings = JSON.parse(settingsJson || '{}');
                  settings.defaultKnowledgeBaseId = e.target.value;
                  onChange?.(JSON.stringify(settings));
                }}
              />
              <p className="text-xs text-muted-foreground">Default knowledge base ID for queries</p>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Default Top K Results</label>
              <Input
                type="number"
                className="w-full"
                value={defaultTopK}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = Number(e.target.value);
                  setDefaultTopK(value);
                  const settings = JSON.parse(settingsJson || '{}');
                  settings.defaultTopK = value;
                  onChange?.(JSON.stringify(settings));
                }}
              />
              <p className="text-xs text-muted-foreground">Number of top results to return (default: 5)</p>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Default Score Threshold</label>
              <Input
                type="number"
                className="w-full"
                step={0.1}
                min={0}
                max={1}
                value={defaultScoreThreshold}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = Number(e.target.value);
                  setDefaultScoreThreshold(value);
                  const settings = JSON.parse(settingsJson || '{}');
                  settings.defaultScoreThreshold = value;
                  onChange?.(JSON.stringify(settings));
                }}
              />
              <p className="text-xs text-muted-foreground">Minimum relevance score (0.0-1.0, default: 0.7)</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
